import { memo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useGoogleMap } from 'js/context/GoogleMapContext';
import { useHookRef } from 'js/hooks/useHookRef';
import useMapEvent from 'js/components/DataLayer/hooks/useMapEvent';
import { voidFunc } from 'js/constants/PropTypeUtils';
import { getLat, getLng } from '../PolygonDrawing/PolygonDrawingUtils';

const MapMarker = (props) => {
  const googleMap = useGoogleMap();
  const marker = useRef(new google.maps.marker.AdvancedMarkerElement());
  const listeners = useRef({
    click: null,
    mouseEnter: null,
    mouseLeave: null,
    dragEnd: null,
  });

  const onClick = useHookRef(
    useCallback(
      (e) => {
        props.onClick(e);
      },
      [props.onClick]
    )
  );

  const onMouseEnter = useHookRef(
    useCallback(
      (e) => {
        props.onMouseEnter(e);
      },
      [props.onMouseEnter]
    )
  );

  const onMouseLeave = useHookRef(
    useCallback(
      (e) => {
        props.onMouseLeave(e);
      },
      [props.onMouseLeave]
    )
  );

  const onDragEnd = useHookRef(
    useCallback(
      (e) => {
        props.onDragEnd(e);
      },
      [props.onDragEnd]
    )
  );

  // Don't show Markers off-screen
  useMapEvent(
    googleMap,
    'idle',
    useCallback(() => {
      if (marker.current) {
        if (googleMap.getBounds().contains(marker.current.position)) {
          marker.current.setMap(googleMap);
        } else {
          marker.current.setMap(null);
        }
      }
    }, [googleMap])
  );

  // Attach listeners on mount
  useEffect(() => {
    Object.values(listeners.current).forEach((listener) => {
      if (listener) {
        google.maps.event.removeListener(listener);
      }
    });

    listeners.current.click = marker.current.addListener('gmp-click', onClick.current);
    listeners.current.mouseEnter = marker.current.addListener('mouseover', onMouseEnter.current);
    listeners.current.mouseLeave = marker.current.addListener('mouseout', onMouseLeave.current);
    listeners.current.dragEnd = marker.current.addListener('dragend', onDragEnd.current);
  });

  useEffect(() => {
    return () => {
      if (marker.current) {
        marker.current.setMap(null);
        marker.current = null;

        Object.values(listeners.current).forEach((listener) => {
          google.maps.event.removeListener(listener);
        });
      }
    };
  }, []);

  // Update marker options when props change
  useEffect(() => {
    if (!props.position) {
      return;
    }

    const icon = document.createElement('img');
    icon.src = props.icon.url;
    if (props.icon.scaledSize) {
      icon.width = props.icon.scaledSize.width;
      icon.height = props.icon.scaledSize.height;
    }
    icon.style.position = 'absolute';
    icon.style.position = 'absolute';
    icon.style.transform = props.icon.anchor
      ? `translate(-${props.icon.anchor.x}px, -${props.icon.anchor.y}px)`
      : 'translate(-50%, -100%)';
    if (!props.clickable) {
      icon.style.pointerEvents = 'none';
    }

    const lat = getLat(props.position);
    const lng = getLng(props.position);

    marker.current.position = { lat, lng };
    marker.current.content = icon;
    marker.current.gmpDraggable = props.draggable;
    marker.current.gmpClickable = props.clickable;
    marker.current.map = googleMap;
    marker.current.zIndex = props.zIndex ? props.zIndex : null;
  }, [props.position, props.icon, props.zIndex, googleMap]);

  return null;
};

MapMarker.propTypes = {
  position: PropTypes.object,
  icon: PropTypes.object,
  draggable: PropTypes.bool,
  zIndex: PropTypes.number,

  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onDragEnd: PropTypes.func,
};

MapMarker.defaultProps = {
  onClick: voidFunc,
  onMouseEnter: voidFunc,
  onMouseLeave: voidFunc,
  onDragEnd: voidFunc,
};

export default memo(MapMarker);
